import React, { useCallback, useEffect, useState } from 'react';
import { Search, UserRoundCheck, Users, X } from 'lucide-react';
import api from '../services/api';

export default function PatientAssignmentsPanel({ professionals = [], onMessage }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [sharingPatientId, setSharingPatientId] = useState(null);
  const [sharedSelection, setSharedSelection] = useState([]);

  const load = useCallback(async (search = '') => {
    try {
      setLoading(true);
      const response = await api.get('/clinic-users/patient-assignments', {
        params: { q: search || undefined, limit: 50 },
      });
      setItems(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      onMessage?.({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível carregar as atribuições.' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => { load(); }, [load]);

  const saveAssignment = async (patient, primaryProfessionalId, assignedProfessionalIds, successText) => {
    try {
      setSavingId(patient.id);
      const response = await api.patch(`/clinic-users/patient-assignments/${patient.id}`, {
        primary_professional_id: primaryProfessionalId || null,
        assigned_professional_ids: assignedProfessionalIds,
      });
      setItems((current) => current.map((item) => item.id === patient.id ? {
        ...item,
        primary_professional_id: response.data?.primary_professional_id || null,
        assigned_professional_ids: response.data?.assigned_professional_ids || [],
      } : item));
      onMessage?.({ type: 'success', text: successText });
      return true;
    } catch (error) {
      onMessage?.({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível atualizar o paciente.' });
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const assignPrimary = async (patient, professionalId) => {
    setSharingPatientId(null);
    const previousPrimary = patient.primary_professional_id || null;
    const shared = (patient.assigned_professional_ids || []).filter((id) => id !== previousPrimary && id !== professionalId);
    const assigned = professionalId ? [professionalId, ...shared] : [];
    await saveAssignment(patient, professionalId, assigned, 'Dentista principal atualizado.');
  };

  const openSharing = (patient) => {
    const primary = patient.primary_professional_id || null;
    setSharedSelection((patient.assigned_professional_ids || []).filter((id) => id !== primary));
    setSharingPatientId(patient.id);
  };

  const toggleShared = (professionalId) => {
    setSharedSelection((current) => current.includes(professionalId)
      ? current.filter((id) => id !== professionalId)
      : [...current, professionalId]);
  };

  const saveSharing = async (patient) => {
    const primary = patient.primary_professional_id || null;
    const assigned = primary ? [primary, ...sharedSelection] : sharedSelection;
    const saved = await saveAssignment(patient, primary, assigned, 'Compartilhamento do paciente atualizado.');
    if (saved) setSharingPatientId(null);
  };

  const professionalName = (professionalId) => professionals.find((item) => item.id === professionalId)?.name || 'Profissional';

  return <div className="bg-white border rounded-xl shadow-sm overflow-hidden mt-6">
    <div className="px-5 py-4 border-b">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2"><UserRoundCheck className="w-5 h-5 text-purple-600" /> Atribuição de pacientes</h2>
      <p className="text-sm text-gray-500 mt-1">Defina o dentista principal e, quando necessário, compartilhe o paciente com outros dentistas.</p>
    </div>
    <form onSubmit={(event) => { event.preventDefault(); load(query); }} className="p-4 border-b flex gap-2">
      <div className="relative flex-1"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nome, CPF, telefone ou e-mail" className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" /></div>
      <button className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">Buscar</button>
    </form>
    {professionals.length === 0 ? <div className="p-6 text-sm text-amber-700 bg-amber-50">Cadastre ao menos um profissional ativo em Configurações antes de atribuir pacientes.</div> : loading ? <div className="p-8 text-center text-gray-500">Carregando...</div> : items.length === 0 ? <div className="p-8 text-center text-gray-500">Nenhum paciente encontrado.</div> : <>
      <div className="divide-y">{items.map((patient) => {
        const sharedIds = (patient.assigned_professional_ids || []).filter((id) => id !== patient.primary_professional_id);
        const sharingOpen = sharingPatientId === patient.id;
        return <div key={patient.id} className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex-1 min-w-0 text-left">
              <p className="font-medium text-gray-900 truncate">{patient.full_name}</p>
              <p className="text-xs text-gray-500 truncate">{patient.email || patient.phone || 'Sem contato informado'}</p>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <select disabled={savingId === patient.id} value={patient.primary_professional_id || ''} onChange={(event) => assignPrimary(patient, event.target.value)} className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" title="Dentista principal">
                  <option value="">Sem dentista principal</option>
                  {professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.name}</option>)}
                </select>
                <button type="button" disabled={savingId === patient.id || !patient.primary_professional_id} title={patient.primary_professional_id ? 'Compartilhar paciente' : 'Defina primeiro o dentista principal'} onClick={() => sharingOpen ? setSharingPatientId(null) : openSharing(patient)} className="inline-flex items-center justify-center gap-2 border rounded-lg px-3 py-2 text-sm text-purple-700 hover:bg-purple-50 disabled:opacity-50"><Users className="w-4 h-4" /> Compartilhar{sharedIds.length ? ` (${sharedIds.length})` : ''}</button>
              </div>
              {sharedIds.length > 0 && <div className="flex flex-wrap justify-end gap-1">{sharedIds.map((id) => <span key={id} className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-700">Compartilhado: {professionalName(id)}</span>)}</div>}
            </div>
          </div>
          {sharingOpen && <div className="mt-4 rounded-lg border border-purple-100 bg-purple-50/50 p-4">
            <div className="flex items-start justify-between gap-3 mb-3"><div><p className="text-sm font-medium text-gray-900">Compartilhar com outros dentistas</p><p className="text-xs text-gray-500 mt-0.5">Os selecionados também poderão acessar este paciente quando usarem “somente atribuídos”.</p></div><button type="button" onClick={() => setSharingPatientId(null)} className="text-gray-400 hover:text-gray-600" aria-label="Fechar compartilhamento"><X className="w-4 h-4" /></button></div>
            <div className="grid sm:grid-cols-2 gap-2">{professionals.map((professional) => {
              const isPrimary = professional.id === patient.primary_professional_id;
              return <label key={professional.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isPrimary ? 'bg-gray-100 text-gray-500' : 'bg-white cursor-pointer hover:border-purple-300'}`}>
                <input type="checkbox" disabled={isPrimary || savingId === patient.id} checked={isPrimary || sharedSelection.includes(professional.id)} onChange={() => toggleShared(professional.id)} />
                <span className="flex-1">{professional.name}</span>{isPrimary && <span className="text-xs">Principal</span>}
              </label>;
            })}</div>
            <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={() => setSharingPatientId(null)} className="border rounded-lg px-3 py-2 text-sm hover:bg-white">Cancelar</button><button type="button" disabled={savingId === patient.id} onClick={() => saveSharing(patient)} className="rounded-lg bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50">{savingId === patient.id ? 'Salvando...' : 'Salvar compartilhamento'}</button></div>
          </div>}
        </div>;
      })}</div>
      <div className="px-5 py-3 border-t text-xs text-gray-500">Exibindo {items.length} de {total} pacientes. Use a busca para localizar outros pacientes.</div>
    </>}
  </div>;
}
